/**
 * Portal v2 reports list (`/portal/reports`).
 *
 * View-only — no upload, no bulk actions. SectionHeader + filter pills
 * (All / Last 7d / Last 30d, role=radiogroup) + CardV2 div-grid table:
 * report id (mono-data), domain, reporter, period, total emails (tnum),
 * pass rate (StatPill).
 *
 * Filtering is client-side against `date_range_begin` — backend exposes
 * page/size only.
 */
import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { format, subDays } from "date-fns";
import { FileText } from "lucide-react";
import { Skeleton, Button } from "@ddmarc/ui";
import { ExportMenu } from "@/components/v2/export-menu";
import { portalApi, type PortalReport } from "@/lib/portal-api";
import { usePortalScope } from "@/lib/portal-scope-context";
import {
  CardV2,
  SectionHeader,
  StatPill,
} from "@/components/primitives";
import { cn } from "@/lib/utils";

type RangeFilter = "all" | "7d" | "30d";

const FILTERS: Array<{ id: RangeFilter; label: string }> = [
  { id: "all", label: "All" },
  { id: "7d", label: "Last 7d" },
  { id: "30d", label: "Last 30d" },
];

const passRateTone = (pct: number): "good" | "warn" | "danger" =>
  pct >= 95 ? "good" : pct >= 80 ? "warn" : "danger";

export function PortalReportsV2Page() {
  const { selectedDomainId } = usePortalScope();
  const [filter, setFilter] = useState<RangeFilter>("all");
  const [page, setPage] = useState(1);
  const size = 50;

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["portal", "reports", { page, size }, selectedDomainId],
    queryFn: () =>
      portalApi.listReports({ page, size, domain_id: selectedDomainId }),
  });

  // Memoised so the `?? []` fallback does not produce a fresh array (and thus
  // a changed dependency) on every render.
  const items = useMemo<PortalReport[]>(() => data?.items ?? [], [data]);
  const filtered = useMemo(() => {
    if (filter === "all") return items;
    const cutoff = subDays(new Date(), filter === "7d" ? 7 : 30);
    return items.filter((r) => new Date(r.date_range_begin) >= cutoff);
  }, [items, filter]);

  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / size));

  return (
    <div className="space-y-3.5">
      <SectionHeader
        title={
          <span className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Reports
          </span>
        }
        subtitle="DMARC aggregate reports from email receivers."
        actions={<ExportMenu kinds={["reports", "summary"]} />}
      />

      <div
        role="radiogroup"
        aria-label="Filter reports by date range"
        className="flex items-center gap-1.5"
      >
        {FILTERS.map((f) => {
          const active = f.id === filter;
          return (
            <button
              key={f.id}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => setFilter(f.id)}
              className={cn(
                "rounded-full px-3 py-1 text-[12px] font-medium transition-colors",
                active
                  ? "bg-foreground text-background"
                  : "bg-muted text-muted-foreground hover:bg-secondary hover:text-foreground"
              )}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      <CardV2 bodyClassName="p-0">
        {isLoading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
            <FileText className="h-10 w-10 text-[var(--v2-fg-faint)]" />
            <p className="text-[13px] font-medium text-foreground">
              No reports {filter === "all" ? "yet" : "in this range"}
            </p>
            <p className="text-[12.5px] text-muted-foreground">
              {filter === "all"
                ? "Reports will appear here once receivers send them."
                : "Try a wider time range."}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            <div className="grid grid-cols-[0.7fr_1.1fr_1.2fr_1.3fr_0.8fr_0.7fr] gap-3 px-4 py-2.5 text-[10.5px] font-semibold uppercase tracking-[0.05em] text-[var(--v2-fg-subtle)]">
              <span>Report</span>
              <span>Domain</span>
              <span>Reporter</span>
              <span>Period</span>
              <span className="text-right">Emails</span>
              <span className="text-right">Pass</span>
            </div>
            {filtered.map((r) => (
              <div
                key={r.id}
                className="grid grid-cols-[0.7fr_1.1fr_1.2fr_1.3fr_0.8fr_0.7fr] items-center gap-3 px-4 py-[var(--v2-row-py,0.625rem)] text-[12.5px]"
              >
                <Link
                  to="/portal/reports/$reportId"
                  params={{ reportId: String(r.id) }}
                  className="font-mono-data truncate text-foreground underline-offset-2 hover:underline"
                >
                  #{r.id}
                </Link>
                <span className="font-mono-data truncate text-foreground">
                  {r.domain}
                </span>
                <span className="truncate text-foreground">{r.org_name}</span>
                <span className="text-muted-foreground">
                  {format(new Date(r.date_range_begin), "MMM d")} –{" "}
                  {format(new Date(r.date_range_end), "MMM d, yyyy")}
                </span>
                <span className="tnum text-right text-foreground">
                  {r.total_emails.toLocaleString()}
                </span>
                <span className="text-right">
                  <StatPill tone={passRateTone(r.pass_rate)}>
                    {r.pass_rate.toFixed(1)}%
                  </StatPill>
                </span>
              </div>
            ))}
          </div>
        )}
      </CardV2>

      {pageCount > 1 && (
        <div className="flex items-center justify-between text-[12px] text-muted-foreground">
          <span>
            Page {page} of {pageCount} · {total.toLocaleString()} total
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1 || isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= pageCount || isFetching}
              onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
